import React, { useEffect, useMemo, useRef, useState } from "react"

import { ReactComponent as IconArrow } from "@common_assets/svg/arrowBack.svg"
import { ReactComponent as SvgPhoto } from "@common_assets/svg/photo.svg"
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material"
import { useMutationStudentHelpers } from "api/reactQuery/mutations/studentHelpers"
import { useInfiniteStudentHelpers } from "api/reactQuery/queries/studentHelpers"
import DialogUpgradeAccount from "components/common/dialogUpgradeAccount/DialogUpgradeAccount"
import ErrorText from "components/common/error/errorText"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import NoMatchesForSearch from "components/common/listing/noMatchesForSearch"
import { Cell, HeadText, Row } from "components/common/table/basicTable"
import BasicTable from "components/common/table/basicTable/BasicTable"
import { useDialog } from "hooks/dialog"
import { useCustomPayment } from "hooks/payment"
import { userIsFreeDemoSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useQueryClient } from "react-query"
import { useParams } from "react-router"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { getErrorMessageFromTab } from "utils/api"

import SearchbarForm from "../../form/searchbarForm"
import {
  OrderType,
  RoleHelper,
  StudentHelpersTypes,
} from "./StudentHelpers.types"

const StudentHelpers = () => {
  const { t } = useTranslation()
  const params = useParams()
  const isFree = userIsFreeDemoSchoolPlan()
  const [sorting, setSorting] = useState<OrderType>("asc")
  const [studentHelperObject, setStudentHelperObject] = useState<
    StudentHelpersTypes[]
  >([])
  const [initialStudentHelperObject, setInitialStudentHelperObject] = useState<
    StudentHelpersTypes[]
  >([])
  const [searchOption, setSearchOption] = useState({
    search: "",
  })

  const { isOpen, handleOpen, handleClose } = useDialog()

  const { showSnackbar } = useCustomSnackbar()

  const queryClient = useQueryClient()

  const { ref: loadMoreRef, inView: isLoadMoreInView } = useInView({
    threshold: 0.5,
  })

  const studentHelpersRef = useRef<HTMLDivElement | null>(null)
  const [isAtBottom, setIsAtBottom] = useState(false)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isLoading: isLoadingStudentHelpers,
  } = useInfiniteStudentHelpers({
    classId: Number(params.classId),
    filter: searchOption.search.length < 2 ? undefined : searchOption.search,
    limit: 20,
    skip: 0,
    sorting: sorting,
    sort_by: "first_name",
  })

  const { payIt, isLoadingPayments } = useCustomPayment()

  const { mutate, isLoading } = useMutationStudentHelpers({
    class_id: Number(params.classId),
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("infiniteStudentHelpers")
        showSnackbar({
          title: "student helpers have been confirmed successfully",
          variant: "success",
        })
      },
      onError: (err) => {
        showSnackbar({
          title: getErrorMessageFromTab(err),
          variant: "error",
        })
      },
    },
  })

  const students = useMemo(
    () => data?.pages.flatMap((item) => item.data.objects) ?? [],
    [data]
  )

  useEffect(() => {
    setStudentHelperObject([])
    setSearchOption({ search: "" })
    setSorting("asc")
  }, [params.classId])

  useEffect(() => {
    isLoadMoreInView && fetchNextPage()
  }, [isLoadMoreInView])

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const isNearBottom = scrollTop + windowHeight >= documentHeight - 100

      setIsAtBottom(isNearBottom)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll() // Check initial state

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [students.length])

  useEffect(() => {
    const initialState = students

    if (initialState?.length) {
      setStudentHelperObject((prev) => {
        const newStudent = initialState
          .filter(
            (item) => !prev.some((exist) => exist.fk_student_id === item.id)
          )
          .map((student) => ({
            fk_student_id: student.id,
            is_banker: student.is_banker,
            is_store_clerk: student.is_store_clerk,
            is_hr_representative: student.is_hr_representative,
          }))
        return [...prev, ...newStudent]
      })

      setInitialStudentHelperObject((prev) => {
        const newStudent = initialState
          .filter(
            (item) => !prev.some((exist) => exist.fk_student_id === item.id)
          )
          .map((student) => ({
            fk_student_id: student.id,
            is_banker: student.is_banker,
            is_store_clerk: student.is_store_clerk,
            is_hr_representative: student.is_hr_representative,
          }))
        return [...prev, ...newStudent]
      })
    }
  }, [students])

  const handleChecked = (id: number, name: RoleHelper) => {
    if (isFree) {
      handleOpen()
      return
    }

    setStudentHelperObject((prev) =>
      prev.map((user) => {
        if (user.fk_student_id === id) {
          return { ...user, [name]: !user[name] }
        }
        return user
      })
    )
  }

  const checkedAction = (id: number, name: RoleHelper) => {
    const student = studentHelperObject?.find(
      (item) => item.fk_student_id === id
    ) as StudentHelpersTypes
    return student ? student[name] : false
  }

  const handleSort = () => {
    const isAsc = sorting === "asc"
    setSorting(isAsc ? "desc" : "asc")
    queryClient.removeQueries(["infiniteStudentHelpers"])
  }

  const renderRoleCheckbox = (
    checkboxId: string,
    role: RoleHelper,
    studentId: number,
    roleLabel: string,
    studentName: string
  ) => (
    <Stack width="100%" alignItems="center">
      <Box
        component="label"
        htmlFor={checkboxId}
        sx={{ cursor: "pointer", display: "inline-flex" }}
      >
        <Checkbox
          id={checkboxId}
          sx={{
            "& .MuiSvgIcon-root": { fontSize: 32 },
          }}
          slotProps={{
            input: {
              "aria-label": `${roleLabel}: ${studentName}`,
            },
          }}
          checked={checkedAction(studentId, role)}
          onChange={() => handleChecked(studentId, role)}
        />
      </Box>
    </Stack>
  )

  // Get students that have changes compared to initial state
  const getChangedStudents = (): StudentHelpersTypes[] => {
    return studentHelperObject.filter((current) => {
      const initial = initialStudentHelperObject.find(
        (item) => item.fk_student_id === current.fk_student_id
      )

      if (!initial) return false

      return (
        current.is_store_clerk !== initial.is_store_clerk ||
        current.is_hr_representative !== initial.is_hr_representative ||
        current.is_banker !== initial.is_banker
      )
    })
  }

  const hasChanges = (): boolean => {
    return getChangedStudents().length > 0
  }

  const isButtonDisabled = () => {
    return isLoading || isFree || !hasChanges()
  }

  const handleScrollToTop = () => {
    studentHelpersRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  return (
    <Stack position="relative" ref={studentHelpersRef}>
      <Stack
        px={2}
        py={3}
       sx={(theme) => ({
    backgroundColor: theme.palette.common.white,
  })}
      >
        <Stack
          flexDirection="row"
          alignItems="center"
          columnGap={2}
          rowGap={2}
          flexWrap="wrap"
        >
          <Stack flex={1} minWidth="250px">
            <SearchbarForm onSubmit={(data) => setSearchOption(data)} />
          </Stack>
          {!!studentHelperObject.length && (
            <Button
              sx={{ width: "max-content" }}
              disabled={isButtonDisabled()}
              onClick={() => {
                const changedStudents = getChangedStudents()
                mutate(changedStudents)
                setInitialStudentHelperObject(studentHelperObject)
              }}
            >
              Confirm
            </Button>
          )}
        </Stack>
      </Stack>
      <BasicTable
        head={
          <Row>
            <Cell tableCellProps={{ width: "auto" }}>
              <TableSortLabel active direction={sorting} onClick={handleSort}>
                <HeadText>{t("studentHelpers.student")}</HeadText>
              </TableSortLabel>
            </Cell>
            <Cell
              tableCellProps={{
                align: "center",
                sx: { width: "200px", minWidth: "100px" },
              }}
            >
              <Stack width="100%">
                <Tooltip
                  placement="top"
                  title={t("studentHelpers.manageSchoolStore")}
                >
                  <Stack>
                    <HeadText textTransform="capitalize">
                      {t("studentHelpers.storeClerk")}:
                    </HeadText>
                    <HeadText textTransform="inherit">
                      {t("studentHelpers.manageClassStore")}
                    </HeadText>
                  </Stack>
                </Tooltip>
              </Stack>
            </Cell>
            <Cell
              tableCellProps={{
                align: "center",
                sx: { width: "200px", minWidth: "100px" },
              }}
            >
              <Stack width="100%">
                <Tooltip
                  placement="top"
                  title={t("studentHelpers.manageHrRepresentative")}
                >
                  <Stack>
                    <HeadText textTransform="capitalize">
                      {t("studentHelpers.hrRepresentative")}:
                    </HeadText>
                    <HeadText textTransform="inherit">
                      {t("studentHelpers.manageJobBoard")}
                    </HeadText>
                  </Stack>
                </Tooltip>
              </Stack>
            </Cell>
            <Cell
              tableCellProps={{
                align: "center",
                sx: { width: "200px", minWidth: "100px" },
              }}
            >
              <Stack width="100%">
                <Tooltip
                  placement="top"
                  title={t("studentHelpers.manageSendTransactions")}
                >
                  <Stack>
                    <HeadText textTransform="capitalize">
                      {t("studentHelpers.banker")}:
                    </HeadText>
                    <HeadText textTransform="inherit">
                      {t("studentHelpers.sendTransactions")}
                    </HeadText>
                  </Stack>
                </Tooltip>
              </Stack>
            </Cell>
          </Row>
        }
        body={
          <TransitionGroup component={null}>
            {students.map((student) => {
              const storeClerkCheckboxId = `student-${student.id}-store-clerk`
              const hrCheckboxId = `student-${student.id}-hr-representative`
              const bankerCheckboxId = `student-${student.id}-banker`

              return (
                <CSSTransition
                  key={student.id}
                  timeout={700}
                  classNames="animation-fade"
                >
                  <Row>
                    <Cell>
                      <Stack
                        flexDirection="row"
                        columnGap={1}
                        alignItems="center"
                      >
                        <Avatar src={student.avatar_url} />
                        <Typography color="mockup.neutral10">
                          {student.first_name} {student.last_name}
                        </Typography>
                      </Stack>
                    </Cell>
                    <Cell>
                      {renderRoleCheckbox(
                        storeClerkCheckboxId,
                        "is_store_clerk",
                        student.id,
                        t("studentHelpers.storeClerk"),
                        `${student.first_name} ${student.last_name}`
                      )}
                    </Cell>
                    <Cell>
                      {renderRoleCheckbox(
                        hrCheckboxId,
                        "is_hr_representative",
                        student.id,
                        t("studentHelpers.hrRepresentative"),
                        `${student.first_name} ${student.last_name}`
                      )}
                    </Cell>
                    <Cell>
                      {renderRoleCheckbox(
                        bankerCheckboxId,
                        "is_banker",
                        student.id,
                        t("studentHelpers.banker"),
                        `${student.first_name} ${student.last_name}`
                      )}
                    </Cell>
                  </Row>
                </CSSTransition>
              )
            })}
          </TransitionGroup>
        }
        bottomSlot={
          <>
            {hasNextPage && <div ref={loadMoreRef} />}
            {isError && <ErrorText />}
            {!isLoadingStudentHelpers &&
              !!searchOption.search.length &&
              students.length === 0 && (
                <NoMatchesForSearch search={searchOption.search} />
              )}
            {!isLoadingStudentHelpers &&
              students.length === 0 &&
              !searchOption.search.length && (
                <NoElementsMessage
                  boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
                  Icon={<SvgPhoto />}
                  title={t("students.youDoNotHaveAnyStudentsYet")}
                  description={t("students.addStudentsManuallyOrHaveStudents")}
                />
              )}
          </>
        }
      ></BasicTable>
      <DialogUpgradeAccount
        open={isOpen}
        onClose={() => {
          handleClose()
          mixpanel.track("reports_export", {
            method: "class_transactions",
            action: "cancel",
          })
        }}
        onActionButtonClick={() => {
          payIt(null)
          mixpanel.track("reports_export", {
            method: "class_transactions",
            action: "submit",
          })
        }}
        isLoading={isLoadingPayments}
        isHelper
      />
      {!isLoadingStudentHelpers &&
        !hasNextPage &&
        isAtBottom &&
        students.length > 1 &&
        (data?.pages.length ?? 0) > 1 && (
          <IconButton
          color="primary"
          size="large"
          aria-label="scroll to top"
          onClick={handleScrollToTop}
          sx={{
            position: "absolute",
            bottom: 24,
            right: 24,
            bgcolor: "common.white",
            boxShadow: 3,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            width: 48,
            height: 48,
            "&:hover": {
              bgcolor: "grey.50",
            },
          }}
        >
          <IconArrow
            style={{
              transform: "rotate(90deg)",
            }}
          />
        </IconButton>
      )}
    </Stack>
  )
}
export default StudentHelpers
